/**
 * MNBP/1 host-side transports (spec §2.7).
 *
 * A HostTransport delivers raw JSON frames from a native client to the
 * BridgeHost and carries responses/events back. Three implementations:
 *
 *  - InPageChannelTransport — embedded WebViews (WebView2 + JavaFX WebView)
 *  - WebSocketHostTransport — the page joins a relay session as `role=host`
 *  - InMemoryTransport       — loopback pair for tests
 */

export interface HostTransportHandlers {
  onFrame(raw: string): void;
  onClose?(): void;
}

export interface HostTransport {
  start(handlers: HostTransportHandlers): void | Promise<void>;
  send(raw: string): void;
  close(): void;
}

type ChannelMode = 'idle' | 'webview2' | 'javafx';

/**
 * Embedded-WebView transport. Installs the page global
 * `window.__MIGIS_BRIDGE__` (spec §2.7.1) and auto-detects the channel:
 *   - WebView2: `window.chrome.webview` present at load
 *   - JavaFX:   the Java side injects `window.__migisNativeChannel` then
 *               calls `__MIGIS_BRIDGE__.channelReady('javafx')`
 */
export class InPageChannelTransport implements HostTransport {
  private handlers: HostTransportHandlers | null = null;
  private _mode: ChannelMode = 'idle';
  private outQueue: string[] = [];
  private modeListeners = new Set<(m: ChannelMode) => void>();

  get mode(): ChannelMode { return this._mode; }

  onModeChange(fn: (m: ChannelMode) => void): void { this.modeListeners.add(fn); }

  start(handlers: HostTransportHandlers): void {
    this.handlers = handlers;
    const g = globalThis as any;

    g.__MIGIS_BRIDGE__ = {
      receive: (json: string) => { this.handlers?.onFrame(String(json)); },
      receiveB64: (b64: string) => {
        try {
          const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
          this.handlers?.onFrame(new TextDecoder().decode(bytes));
        } catch {
          // Malformed base64 — surface as a parse error via a bogus frame.
          this.handlers?.onFrame('\u0000');
        }
      },
      channelReady: (kind: string) => {
        if (kind === 'javafx') this.setMode('javafx');
      },
    };

    const webview = g.chrome?.webview;
    if (webview?.postMessage) {
      webview.addEventListener?.('message', (e: any) => {
        const raw = typeof e.data === 'string' ? e.data : JSON.stringify(e.data);
        this.handlers?.onFrame(raw);
      });
      this.setMode('webview2');
    }
  }

  private setMode(mode: ChannelMode): void {
    this._mode = mode;
    this.modeListeners.forEach((fn) => { try { fn(mode); } catch { /* listener error */ } });
    const q = this.outQueue.splice(0);
    q.forEach((raw) => this.send(raw));
  }

  send(raw: string): void {
    const g = globalThis as any;
    if (this._mode === 'webview2' && g.chrome?.webview?.postMessage) {
      g.chrome.webview.postMessage(raw);
      return;
    }
    if (this._mode === 'javafx' && g.__migisNativeChannel?.send) {
      g.__migisNativeChannel.send(raw);
      return;
    }
    // Channel not up yet — queue (bounded).
    if (this.outQueue.length < 512) this.outQueue.push(raw);
  }

  close(): void {
    const g = globalThis as any;
    if (g.__MIGIS_BRIDGE__) delete g.__MIGIS_BRIDGE__;
    this.handlers?.onClose?.();
    this.handlers = null;
    this._mode = 'idle';
  }
}

/** Browser-side WebSocket transport: the embed page joins a relay session as host. */
export class WebSocketHostTransport implements HostTransport {
  private ws: WebSocket | null = null;
  private outQueue: string[] = [];
  private closed = false;

  constructor(private url: string) {}

  start(handlers: HostTransportHandlers): Promise<void> {
    return new Promise((resolve, reject) => {
      const WS: typeof WebSocket = (globalThis as any).WebSocket;
      if (!WS) { reject(new Error('WebSocket unavailable')); return; }
      const ws = new WS(this.url);
      this.ws = ws;
      ws.onopen = () => {
        this.outQueue.splice(0).forEach((raw) => ws.send(raw));
        resolve();
      };
      ws.onmessage = (e) => handlers.onFrame(typeof e.data === 'string' ? e.data : ''); 
      ws.onerror = () => { if (!this.closed) reject(new Error('WebSocket error')); };
      ws.onclose = () => { handlers.onClose?.(); };
    });
  }

  send(raw: string): void {
    if (this.ws && this.ws.readyState === 1 /* OPEN */) this.ws.send(raw);
    else if (this.outQueue.length < 512) this.outQueue.push(raw);
  }

  close(): void {
    this.closed = true;
    this.ws?.close();
    this.ws = null;
  }
}

/**
 * Loopback pair for tests: `const [a, b] = createInMemoryTransportPair()`.
 * Frames sent on one side arrive (async microtask) at the other side's
 * onFrame. Both ends satisfy HostTransport; tests use one end as the host
 * transport and drive the other end as a fake native client.
 */
export class InMemoryTransport implements HostTransport {
  private handlers: HostTransportHandlers | null = null;
  peer: InMemoryTransport | null = null;

  start(handlers: HostTransportHandlers): void { this.handlers = handlers; }

  send(raw: string): void {
    const peer = this.peer;
    queueMicrotask(() => peer?.handlers?.onFrame(raw));
  }

  close(): void {
    this.handlers?.onClose?.();
    this.peer?.handlers?.onClose?.();
  }
}

export function createInMemoryTransportPair(): [InMemoryTransport, InMemoryTransport] {
  const a = new InMemoryTransport();
  const b = new InMemoryTransport();
  a.peer = b;
  b.peer = a;
  return [a, b];
}
